/**
 * hidden-variant-fix.js
 *
 * The compiled theme.js `VariantPicker.selectVariant()` decides whether a variant is
 * selectable using only `variant.available`. It has no concept of YoungLA's
 * `custom.hidden` variant metafield, so an individually hidden variant (a combination
 * whose color and size each still have other, visible variants) can be selected directly.
 *
 * We can't edit the compiled bundle, and the relevant resolution logic lives in private
 * (closure) methods that aren't reachable on the prototype. Instead we wrap the public
 * `selectVariant(id)` method: when the requested id is missing or points at a hidden
 * variant, we substitute the best matching non-hidden variant before delegating to the
 * original implementation.
 *
 * Hidden variant ids are provided per picker via the `data-hidden-variant-ids` attribute
 * rendered in snippets/variant-picker.liquid.
 */
(() => {
  const TAG = 'variant-picker';

  customElements.whenDefined(TAG).then(() => {
    const VariantPicker = customElements.get(TAG);
    if (!VariantPicker || VariantPicker.prototype.__hiddenVariantFixApplied) {
      return;
    }
    VariantPicker.prototype.__hiddenVariantFixApplied = true;

    const originalSelectVariant = VariantPicker.prototype.selectVariant;

    const getHiddenIds = (picker) => {
      const raw = picker.getAttribute('data-hidden-variant-ids') || '';
      return new Set(
        raw
          .split(',')
          .map((value) => parseInt(value, 10))
          .filter((value) => !Number.isNaN(value))
      );
    };

    // Read the currently selected option values straight from the DOM, mirroring
    // theme.js `getSelectedOptionValues`.
    const getSelectedOptionValues = (picker) => {
      return Array.from(picker.querySelectorAll('[data-option-selector]')).map((selector) => {
        const input = selector.querySelector(
          'input[name^="option"][type="hidden"], input[name^="option"]:checked, select[name^="option"]'
        );
        return input ? input.value : null;
      });
    };

    // Find the best non-hidden variant: prefer an exact option match, then progressively
    // relax the trailing options (mirroring theme.js's own fallback), honouring the
    // hide-sold-out setting.
    const findVisibleVariant = (picker, hiddenIds) => {
      const variants = picker.product && picker.product.variants;
      if (!variants) {
        return null;
      }

      const options = getSelectedOptionValues(picker);
      const hideSoldOut = picker.hasAttribute('hide-sold-out-variants');

      for (let keep = options.length; keep >= 0; keep--) {
        const match = variants.find((variant) => {
          if (hiddenIds.has(variant.id)) {
            return false;
          }
          if (hideSoldOut && !variant.available) {
            return false;
          }
          return variant.options.slice(0, keep).every((value, index) => value === options[index]);
        });

        if (match) {
          return match;
        }
      }

      return null;
    };

    VariantPicker.prototype.selectVariant = function (id) {
      const hiddenIds = getHiddenIds(this);

      // Nothing hidden on this product, or product data not ready — behave exactly as stock.
      if (hiddenIds.size === 0 || !this.product) {
        return originalSelectVariant.call(this, id);
      }

      const numericId = parseInt(id, 10);

      if (id == null || hiddenIds.has(numericId)) {
        const replacement = findVisibleVariant(this, hiddenIds);
        if (replacement) {
          id = replacement.id;
        }
      }

      return originalSelectVariant.call(this, id);
    };

    // Correct any picker that was server-rendered onto a hidden variant on initial load.
    customElements.upgrade(document.body);
    document.querySelectorAll(TAG).forEach((picker) => {
      const hiddenIds = getHiddenIds(picker);
      if (hiddenIds.size === 0 || !picker.product) {
        return;
      }
      const current = picker.selectedVariant;
      if (current && hiddenIds.has(current.id)) {
        picker.selectVariant(current.id);
      }
    });
  });
})();
